import { FC, ReactNode, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import svgIcons from "../assets/svg/svgIcons";
import { APP_CONFIG } from "../constants/constants";
import AdminTableSortable from "./adminTableSortable";
import { TAdminTableRowData } from "./types";
import { DragDropContext, Draggable, DraggingStyle, Droppable, DropResult, NotDraggingStyle } from "@hello-pangea/dnd";

interface AdminTableProps {
  columns: string[];
  compact?: boolean;
  customTableHeadStyles?: string;
  customWrapperClass?: string;
  data: TAdminTableRowData[] | null;
  heightLimitClass?: string;
  isLoading: boolean;
  listingQueryParams?: any;
  listingWrapperRef?: React.RefObject<HTMLDivElement>;
  onOrderingChange?: (ordering?: string) => void;
  ordering?: string;
  rightAlignLastColumn?: boolean;
  sortableColumns?: Record<string, string>;
  viewportHeightOffset?: "400" | "500" | "600" | null;
  useSortableColumns?: boolean;
  disabled?: boolean;
  renderRowActions?: (rowIndex: number) => React.ReactNode;
  posAbsoluteLoader?: boolean;
  colors?: Record<string,string>;
  draggableColumns?: boolean;
  onColumnOrderChange?: (column:string[])=>void;
}

interface OptionalDragDropContextProps {
  children: ReactNode;
  enabled: boolean;
  onDragEnd: (result: DropResult) => void;
}

const OptionalDragDropContext: FC<OptionalDragDropContextProps> = ({
  children,
  enabled,
  onDragEnd,
}) =>
  enabled ? (
    <DragDropContext onDragEnd={onDragEnd}>{children}</DragDropContext>
  ) : (
    <>{children}</>
  );

const AdminTable: FC<AdminTableProps> = ({
  columns,
  compact = false,
  customTableHeadStyles = "",
  customWrapperClass = "overflow-auto relative",
  data,
  heightLimitClass = "",
  isLoading = false,
  listingQueryParams = {},
  listingWrapperRef = null,
  onOrderingChange,
  ordering,
  rightAlignLastColumn = false,
  sortableColumns = {},
  viewportHeightOffset = null,
  useSortableColumns = false,
  disabled,
  renderRowActions = () => null,
  posAbsoluteLoader = false,
  colors,
  draggableColumns = false,
  onColumnOrderChange=()=>null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let wrapperHeightClass = "";
  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );
  switch (viewportHeightOffset) {
    case "400":
      // wrapperHeightClass = 'max-h-[calc(100vh-400px)]';
      wrapperHeightClass = userIsImpersonated
        ? "max-h-[calc(100vh-420px)]" // + 40px for impersonation (blue) bar
        : "max-h-[calc(100vh-380px)]"; // 48+32+52+16+40+42+24+16+16+38+28= 352 ~= 360 + 10 (10px of bottom spacing) = 370
      break;
    case "500":
      wrapperHeightClass = "max-h-[calc(100vh-500px)]"; // TODO: check later if this is being used
      break;
    case "600":
      wrapperHeightClass = "max-h-[calc(100vh-600px)]"; // TODO: check later if this is being used
      break;
    default:
      wrapperHeightClass = "";
  }
  const wrapperClass = `${customWrapperClass} ${heightLimitClass} ${wrapperHeightClass}`;
  const lastColumnAlignmentClass = rightAlignLastColumn
    ? "text-right"
    : "text-left";

  const [orderedColumns, setOrderedColumns] = useState<string[]>(columns);

  useEffect(() => {
    setOrderedColumns(columns);
  }, [columns]);

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    if (source.index === destination.index) {
      return;
    }
    const newColumns = Array.from(orderedColumns);
    const [movedColumn] = newColumns.splice(source.index, 1);
    newColumns.splice(destination.index, 0, movedColumn);
    setOrderedColumns(newColumns);
    onColumnOrderChange(newColumns);
  };
  const getItemStyle = (style: DraggingStyle | NotDraggingStyle | undefined) => {
    if (!style) return style;
    if ("transform" in style && style.transform) {
      const transform = style.transform.replace(/,\s*-?\d+(\.\d+)?px\)/, ", 0px)");
      return { ...style, transform };
    }
    return style;
  };

  const handleSort = (column: string) => {
    const orderingField = sortableColumns[column];

    if (!orderingField || !onOrderingChange) return;

    if (ordering !== orderingField && ordering !== `-${orderingField}`) {
      onOrderingChange(`-${orderingField}`);
    } else if (ordering === `-${orderingField}`) {
      onOrderingChange(orderingField);
    } else {
      onOrderingChange(undefined);
    }
  };

  if (!!useSortableColumns) {
    return (
      <AdminTableSortable
        columns={columns}
        compact={compact}
        customTableHeadStyles={customTableHeadStyles}
        customWrapperClass={customWrapperClass}
        data={data}
        heightLimitClass={heightLimitClass}
        isLoading={isLoading}
        listingQueryParams={listingQueryParams}
        listingWrapperRef={listingWrapperRef ?? undefined}
        rightAlignLastColumn={rightAlignLastColumn}
        viewportHeightOffset={viewportHeightOffset}
      />
    );
  }

  return (
    <div className={wrapperClass} ref={listingWrapperRef}>
      <div
        className={`${posAbsoluteLoader ? "absolute top-0 left-0 max-w-full max-h-full" : "sticky top-1/2"} z-listing-table min-w-full h-full${isLoading ? "" : " hidden"}`}
      >
        <img
          src={svgIcons.LoadingAnimation}
          alt="loading-animation"
          className="size-16 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        />
      </div>
      <OptionalDragDropContext
        enabled={draggableColumns}
        onDragEnd={handleDragEnd}
      >
        <table
          className={`w-full transition border-collapse border-spacing-0${isLoading ? " opacity-25 pointer-events-none touch-none" : ""}`}
        >
          <thead
            className={`sticky top-0 bg-white border-b border-gray-500 ${customTableHeadStyles}`}
          >
            {draggableColumns ?
              <Droppable droppableId="table-headers" direction="horizontal">
                {(provided, snapshot) => (
                  <tr
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={snapshot.isDraggingOver ? "bg-blue-50" : ""}
                  >
                    {orderedColumns.map((column, index) => {
                      const orderingField = sortableColumns[column];
                      const isSortable = !!orderingField && !!onOrderingChange;
                      return (
                        <Draggable key={column} draggableId={column} index={index}>
                          {(dragProvided, dragSnapshot) => (
                            <th
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              // {...dragProvided.dragHandleProps}
                              style={getItemStyle(dragProvided.draggableProps.style)}
                              className={`text-left text-sm font-semibold leading-6 whitespace-nowrap p-0${index === orderedColumns.length - 1 ? ` ${lastColumnAlignmentClass}` : ""}${dragSnapshot.isDragging ? " bg-blue-100" : ""}`}
                            >
                              <div className={`relative flex items-center border-b-2 border-gray-400 py-1 w-full group ${compact ? "px-2" : ""}`}>
                                <span
                                  {...dragProvided.dragHandleProps}
                                  className="cursor-grab text-gray-300 hover:text-gray-500 shrink-0 ml-2"
                                  aria-label={`Drag to reorder ${column}`}
                                >
                                  ⠿
                                </span>

                                {isSortable ? (
                                  <button
                                    type="button"
                                    className="flex items-center w-full cursor-pointer"
                                    onClick={() => handleSort(column)}
                                  >
                                    <span
                                      className={`flex items-center gap-2 rounded-lg group-hover:bg-accent-blue-paleO66${!!ordering && ordering.includes(orderingField)
                                        ? ` bg-accent-blue-paleO66`
                                        : ""
                                        } ${compact
                                          ? "px-0"
                                          : index === orderedColumns.length - 1
                                            ? "pl-3 pr-16"
                                            : "px-4"
                                        }`}
                                    >

                                      <span className="text-l font-semibold text-black leading-10">
                                        {column}
                                      </span>
                                      <span className="flex flex-col w-4">
                                        {ordering === orderingField ? (
                                          <>
                                            <img
                                              src={svgIcons.CollapseIcon}
                                              alt={column}
                                              className="size-4 group-hover:grayscale group-hover:opacity-15"
                                            />
                                            <img
                                              src={svgIcons.ExpandMoreIcon}
                                              alt={column}
                                              className="size-4 grayscale opacity-50 group-hover:grayscale group-hover:opacity-15"
                                            />
                                          </>
                                        ) : ordering === `-${orderingField}` ? (
                                          <>
                                            <img
                                              src={svgIcons.CollapseIcon}
                                              alt={column}
                                              className="size-4 grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100"
                                            />
                                            <img
                                              src={svgIcons.ExpandMoreIcon}
                                              alt={column}
                                              className="size-4 group-hover:grayscale group-hover:opacity-15"
                                            />
                                          </>
                                        ) : (
                                          <>
                                            <img
                                              src={svgIcons.CollapseIcon}
                                              alt={column}
                                              className="size-4 grayscale opacity-50 group-hover:opacity-15"
                                            />
                                            <img
                                              src={svgIcons.ExpandMoreIcon}
                                              alt={column}
                                              className="size-4 grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100"
                                            />
                                          </>
                                        )}
                                      </span>
                                    </span>
                                  </button>
                                ) : (
                                  <span
                                    className={`block ${compact
                                      ? "px-2 py-1"
                                      : `py-3 ${index === orderedColumns.length - 1
                                        ? "pl-3 pr-16"
                                        : "px-4"
                                      }`
                                      } border-b-2 border-gray-400`}
                                  >
                                    {column}
                                  </span>
                                )}
                              </div>
                            </th>
                          )}
                        </Draggable>
                      );
                    })}
                    <td style={{ padding: 0, border: "none", width: 0 }}>
                      {provided.placeholder}
                    </td>
                  </tr>
                )}
              </Droppable>
              :
              <tr>
                {columns?.map((column, index) =>
                  (() => {
                    const orderingField = sortableColumns[column];
                    const isSortable = !!orderingField && !!onOrderingChange;

                    return (
                      <th
                        key={index}
                        className={`text-left text-sm font-semibold leading-6 whitespace-nowrap p-0${index === columns.length - 1 ? ` ${lastColumnAlignmentClass}` : ""}`}
                      >
                        {isSortable ? (
                          <button
                            type="button"
                            className={`relative flex items-center border-b-2 border-gray-400 py-1 w-full group cursor-pointer ${compact ? "px-2" : ""
                              }`}
                            onClick={() => handleSort(column)}
                          >
                            <span
                              className={`flex items-center gap-2 rounded-lg group-hover:bg-accent-blue-paleO66${
                                /* this bg color indicates that the results are sorted by which column */
                                !!ordering && ordering.includes(orderingField)
                                  ? ` bg-accent-blue-paleO66`
                                  : ""
                                } ${compact
                                  ? "px-0"
                                  : index === columns.length - 1
                                    ? "pl-3 pr-16"
                                    : "px-4"
                                }`}
                            >
                              <span className="text-l font-semibold text-black leading-10">
                                {column}
                              </span>
                              <span className="flex flex-col w-4">
                                {ordering === orderingField ? (
                                  <>
                                    <img
                                      src={svgIcons.CollapseIcon}
                                      alt={column}
                                      className="size-4 group-hover:grayscale group-hover:opacity-15"
                                    />
                                    <img
                                      src={svgIcons.ExpandMoreIcon}
                                      alt={column}
                                      className="size-4 grayscale opacity-50 group-hover:grayscale group-hover:opacity-15"
                                    />
                                  </>
                                ) : ordering === `-${orderingField}` ? (
                                  <>
                                    <img
                                      src={svgIcons.CollapseIcon}
                                      alt={column}
                                      className="size-4 grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100"
                                    />
                                    <img
                                      src={svgIcons.ExpandMoreIcon}
                                      alt={column}
                                      className="size-4 group-hover:grayscale group-hover:opacity-15"
                                    />
                                  </>
                                ) : (
                                  <>
                                    <img
                                      src={svgIcons.CollapseIcon}
                                      alt={column}
                                      className="size-4 grayscale opacity-50 group-hover:opacity-15"
                                    />
                                    <img
                                      src={svgIcons.ExpandMoreIcon}
                                      alt={column}
                                      className="size-4 grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100"
                                    />
                                  </>
                                )}
                              </span>
                            </span>
                          </button>
                        ) : (
                          <span
                            className={`block ${compact
                                ? "px-2 py-1"
                                : `py-3 ${index === columns.length - 1
                                  ? "pl-3 pr-16" // pr-16 prevents the last column header text from being overlapped by the edit columns button
                                  : "px-4"
                                }`
                              } border-b-2 border-gray-400`}
                          >
                            {column}
                          </span>
                        )}
                      </th>
                    );
                  })()
                )}
              </tr>
            }
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 border-b border-gray-200">
            {!!data && data.length > 0 ? (
              data?.map((rowData: TAdminTableRowData, index: number) => {
                const rowTextColorClass =
                  rowData.isActive === false
                    ? "text-gray-700"
                    : "text-gray-700";
                const rowActionsContent = renderRowActions(index);
                const shouldShowRelativeClass = !!rowActionsContent;
                return (
                  <tr
                    key={rowData.tableRowId || `row_${index}`}
                    id={rowData?.tableRowId || undefined}
                    className={
                      !!rowData.navLink && typeof rowData.navLink !== "boolean"
                        ? `cursor-pointer hover:bg-accent-blue-paleO66`
                        : ""
                    }
                    onClick={() => {
                      if (
                        !!rowData.navLink &&
                        typeof rowData.navLink !== "boolean" &&
                        !disabled
                      )
                        if (typeof rowData.navLink === "function") {
                          rowData.navLink?.();
                        } else {
                          navigate(rowData.navLink, {
                            state: {
                              ...listingQueryParams,
                              ...(rowData?.stateData || {}),
                            },
                          });
                        }
                    }}
                  >
                    {orderedColumns.map((column, i) => {
                      const originalIndex = columns.indexOf(column);
                      const cellData = rowData.cellData?.[originalIndex];
                      return (
                        <td
                          key={`row_${index}_cell_${i}`}
                          className={`${compact ? "px-2 py-1" : "px-4 py-3"} whitespace-nowrap text-xs font-medium leading-6 ${rowTextColorClass} border-t border-gray-200${i === orderedColumns.length - 1
                            ? `${lastColumnAlignmentClass}${shouldShowRelativeClass ? " relative" : ""}`
                            : ""
                            }`}
                          style={{
                            color:
                              colors && typeof cellData === "string" && cellData in colors
                                ? colors[cellData as keyof typeof colors]
                                : undefined,
                          }}
                        >
                      {/* {i === rowData.cellData.length - 1 && (
                        <div className="relative">
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            Buttons
                          </div>
                        </div>
                      )} */}

                      {i === rowData.cellData.length - 1 &&
                            renderRowActions &&
                            renderRowActions(index)}

                      {/* extra debugging span */}
                          {APP_CONFIG.DEBUG.DISPLAY_VERBOSE &&
                            !!rowData?.cellDataMeta?.[originalIndex] && (
                              <span className="relative">
                                <pre className="absolute bottom-full text-[8px] leading-[8px] text-black/50">
                                  {rowData?.cellDataMeta?.[originalIndex]}
                                </pre>
                              </span>
                            )}

                          {cellData}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : isLoading ? (
              <tr>
                <td
                  colSpan={orderedColumns.length}
                  className="text-center py-24 text-lg font-bold text-gray-400"
                >
                  &nbsp;
                </td>
              </tr>
            ) : (
              <tr>
                <td
                  colSpan={orderedColumns.length}
                  className="text-center py-24 text-lg font-bold text-gray-400"
                >
                  {t("no_items_found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </OptionalDragDropContext>
    </div>
  );
};

export default AdminTable;
